import { useCallback, useEffect, useMemo, useRef } from 'react';

import { fetchDeposits } from 'api/apiExchange';
import {
  useActiveWindowInterval,
  useWebsocketEventListener,
} from 'components/hooks';
import config from 'config';
import { DEPOSIT_MINED_FETCH_DELAY_MS } from 'config/constants';
import { hoursDifferenceLongerThan } from 'lib/date';
import { getAnonymousRPCProvider } from 'lib/ethereum/wallets/utils';
import { waitAsync } from 'lib/promiseTools';
import {
  updatePendingDepositConfirmations,
  updateBridgedPendingDeposit,
  removePendingDeposit,
} from 'reduxState/balances/pendingDeposits';
import {
  notifyAboutCompletedDeposit,
  notifyAndUpdatePendingDepositStatusToFailed,
  useSelectPendingDepositsListIncludingFailed,
} from 'reduxState/balances/pendingDepositsUtils';
import { PendingDepositItem } from 'reduxState/balances/pendingDepositTypes';
import { useSelector, staticDispatch } from 'reduxState/store';

import { MIN_DEPOSIT_QUANTITY_MULTIPLIER } from '../bridge/stargate/stargateConstants';
import { getStargateSwapStatus } from '../bridge/stargate/stargateStatus';

const blockConfirmationDelay = config.chains.katanaChain.blocksConfirmation;

/**
 * Deposit overlay is responsible for displaying modal on transactions being successuflly mined, or rejected by network.
 *
 * This coomponent should be always mount on top level,
 * so it can fetch pending transaction information.
 */
function DepositBackgroundWorker() {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const pendingDepositsList = useSelectPendingDepositsListIncludingFailed();
  const pendingConfirmationsCount = useSelector(
    state => state.balances.pendingDeposits.confirmationsCounts
  );
  const vaultDepositFee = useSelector(
    state => state.exchange.info.vaultDepositFee
  );

  const pendingDepositsTxIds = useMemo(
    () =>
      pendingDepositsList
        // .filter(deposit => !hoursDifferenceLongerThan(deposit.time, Date.now(), 3))
        .map(deposit => [
          deposit.sourceTxId?.toLowerCase(),
          deposit.bridgeTxId?.toLowerCase(),
        ]),
    [pendingDepositsList]
  );

  // Check confirmed in the API
  useEffect(() => {
    if (!walletAddress || pendingDepositsTxIds.length <= 0) {
      return;
    }
    fetchDeposits({
      wallet: walletAddress,
      limit: 10,
    })
      .then(persistedDepositsApi => {
        pendingDepositsTxIds.forEach(([sourceTx, bridgeTx]) => {
          const completedDeposit = persistedDepositsApi.find(depositInApi => {
            const apiTx =
              depositInApi.bridgeTxId?.toLowerCase() ||
              depositInApi.forwarderTxId?.toLowerCase() ||
              depositInApi.katanaTxId?.toLowerCase();
            return bridgeTx === apiTx || sourceTx === apiTx;
          });
          const txConfirmedInApi = sourceTx && completedDeposit;
          if (txConfirmedInApi) {
            console.log('[deposit] Confirmed in the API');
            // User could miss WebSocket notification (eg. opening web later)
            const creditedEarlier = Date.now() > completedDeposit.time + 20_000; // 20 seconds
            if (creditedEarlier) {
              notifyAboutCompletedDeposit(completedDeposit.quantity);
            }
            staticDispatch(removePendingDeposit(sourceTx));
          }
        });
      })
      .catch(error => {
        console.warn(error);
      });
  }, [pendingDepositsTxIds, walletAddress]);

  /** Deposits for check confirmations */
  const pendingDepositsWithNonMaxConfirmations = useMemo(
    () =>
      blockConfirmationDelay
        ? pendingDepositsList.filter(deposit => {
            const confirmations = pendingConfirmationsCount[deposit.sourceTxId];
            if (confirmations === -1) {
              return false;
            }
            const bridgedConfirmations =
              pendingConfirmationsCount[deposit.bridgeTxId || '-'];
            if (bridgedConfirmations === -1) {
              return false;
            }
            const requiredConfirmations = deposit.bridge
              ? 1 // For initial bridge TX, we only need single confirmation
              : blockConfirmationDelay;
            return deposit.bridge && deposit.bridgeTxId
              ? (bridgedConfirmations || 0) < blockConfirmationDelay
              : (confirmations || 0) < requiredConfirmations;
          })
        : [],
    [pendingConfirmationsCount, pendingDepositsList]
  );

  const pendingCrossChainWithoutBridgedTx = useMemo(
    () =>
      blockConfirmationDelay
        ? pendingDepositsList.filter(
            deposit =>
              deposit.bridge &&
              // Only those which are already mined initial TX
              pendingConfirmationsCount[deposit.sourceTxId] &&
              !deposit.bridgeTxId
          )
        : [],
    [pendingConfirmationsCount, pendingDepositsList]
  );

  const bridgedCheckAsync = useCallback(
    async (deposit: PendingDepositItem) => {
      if (
        pendingConfirmationsCount[deposit.sourceTxId] &&
        deposit.bridge === 'Stargate'
      ) {
        const stargateStatus = await getStargateSwapStatus(
          deposit.sourceTxId,
          deposit.forwarderTxId
        );
        if (
          stargateStatus?.destinationTxHash ||
          stargateStatus?.forwarderLzComposeTxHash
        ) {
          staticDispatch(
            updateBridgedPendingDeposit(
              {
                txHash: deposit.sourceTxId,
                forwarderTxId: stargateStatus.forwarderLzComposeTxHash,
                bridgeTxId: stargateStatus.destinationTxHash,
              }
              // stargateStatus.message.srcUaNonce,
            )
          );
        }
        if (stargateStatus?.status === 'FAILED') {
          staticDispatch(
            notifyAndUpdatePendingDepositStatusToFailed(deposit.sourceTxId)
          );
        }
      }
    },
    [pendingConfirmationsCount]
  );

  /** Prevent multiple checks running in parallel, if network is slow */
  const depositCheckPending = useRef(false);

  useActiveWindowInterval(
    async function depositsWorker() {
      if (!walletAddress || depositCheckPending.current) {
        return;
      }
      depositCheckPending.current = true;
      const depositsToCheck = pendingDepositsWithNonMaxConfirmations.filter(
        deposit => {
          const isOld = hoursDifferenceLongerThan(
            deposit.time,
            Date.now(),
            24 * 5 /* 5 days */
          );
          // Stop doing realtime check if deposit is stuck.
          // There will be done API check once user reload page.
          if (isOld) {
            console.warn(
              `Skipping check - Stuck deposit ${deposit.sourceTxId}`
            );
          }
          return !isOld;
        }
      );
      for (const deposit of depositsToCheck) {
        const txHashToCheck = deposit.bridgeTxId
          ? deposit.bridgeTxId
          : deposit.sourceTxId;

        // Update not confirmed first by blockchain (Until we have at least one confirmation)
        await getAnonymousRPCProvider(
          config.chains[
            deposit.bridgeTxId ? 'katanaChain' : deposit.sourceChain
          ]
        )
          // Warn: this is doing 4 RPC requests
          .getTransactionReceipt(txHashToCheck)
          .then(async receipt => {
            if (!receipt) {
              return;
            }
            console.log('TX status:', receipt);
            const confirmed: number =
              !receipt || receipt.status === null
                ? 0 // Not expected
                : receipt.status <= 0 // Post-byzantium blocks will have a status (0 = failure)
                ? -1
                : await receipt.confirmations();
            console.log('Confirmed: ', txHashToCheck, confirmed);
            staticDispatch(
              confirmed === -1
                ? notifyAndUpdatePendingDepositStatusToFailed(txHashToCheck)
                : updatePendingDepositConfirmations(txHashToCheck, confirmed)
            );
            if (confirmed && deposit.bridge && !deposit.bridgeTxId) {
              return bridgedCheckAsync(deposit);
            }
          })
          .catch(error => {
            console.warn(error);
          });
      }

      for (const deposit of pendingCrossChainWithoutBridgedTx) {
        await bridgedCheckAsync(deposit);
      }

      depositCheckPending.current = false;
    },
    15 * 1000 * (pendingDepositsWithNonMaxConfirmations.length || 1)
  );

  useWebsocketEventListener('deposits', async depositWs => {
    const receivedQuantity = Number(depositWs.data.quantity);
    const possibleDepositsList = pendingDepositsList.filter(deposit => {
      const sourceQuantity = Number(deposit.quantity);
      const minQuantity =
        sourceQuantity * MIN_DEPOSIT_QUANTITY_MULTIPLIER -
        (deposit.vault ? Number(vaultDepositFee || 0) : 0);
      return (
        receivedQuantity >= minQuantity && receivedQuantity <= sourceQuantity
      );
    });
    if (possibleDepositsList.length <= 0) {
      return;
    }
    const txIds = possibleDepositsList.map(deposit => [
      deposit.sourceTxId,
      deposit.bridgeTxId,
    ]);
    await waitAsync(DEPOSIT_MINED_FETCH_DELAY_MS);
    try {
      const persistedDeposits = await fetchDeposits({
        wallet: walletAddress,
        limit: 4,
      });
      persistedDeposits.forEach(deposit => {
        const foundPair = txIds.find(([sourceTx, bridgeTx]) =>
          deposit.bridgeTxId
            ? bridgeTx === deposit.bridgeTxId
            : sourceTx === deposit.katanaTxId
        );
        if (foundPair && foundPair[0]) {
          // Notification already displayed by notificationSdk (WebSocket)
          staticDispatch(removePendingDeposit(foundPair[0]));
        }
      });
    } catch (error) {
      console.warn(error);
    }
  });

  return null;
}

export default DepositBackgroundWorker;
